import { useCallback, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import type { PlanFieldItem } from "../../../../BookingForm/components/Commercial/add-plan";
import { createEmptyCommercialPlanField } from "../../../../BookingForm/components/Commercial/add-plan";
import { useBookingSelector } from "../../../../BookingForm/hooks/useBookingSelector";
import type { PaymentPlansStepFormValues } from "./types";
import { useTorRules } from "../../../../BookingForm/hooks/useTorRules";

type UsePaymentPlansMutationsProps = {
  yearLimit: number;
};

export const usePaymentPlansMutations = (props: UsePaymentPlansMutationsProps) => {
  const { yearLimit } = props;
  const bookingDetails = useBookingSelector();
  const { isTurnoverRentOnly } = useTorRules();
  const { control } = useFormContext<PaymentPlansStepFormValues>();

  const {
    fields: plansFields,
    append: plansAppend,
    remove: plansRemove,
    replace: plansReplace,
  } = useFieldArray({ control, name: "plans" });

  // Remount key for AddPlanAccordion (add/remove UI); no longer bumped on invalidate.
  const [paymentPlanResetKey] = useState(0);

  const handleRemovePlan = useCallback(
    (index: number) => {
      plansRemove(index);
    },
    [plansRemove],
  );

  const handleAddPlan = useCallback(() => {
    if (yearLimit <= plansFields.length) return;
    plansAppend(createEmptyCommercialPlanField(isTurnoverRentOnly) as PlanFieldItem);
  }, [isTurnoverRentOnly, plansAppend, plansFields.length, yearLimit]);

  const canAddPlan = useMemo(
    () => bookingDetails?.paymentPlanApproval !== "sent" && yearLimit > plansFields.length,
    [bookingDetails?.paymentPlanApproval, plansFields.length, yearLimit],
  );

  return {
    plansFields,
    plansReplace,
    handleRemovePlan,
    handleAddPlan,
    paymentPlanResetKey,
    canAddPlan,
  };
};
